/**
 * File responsible for mapping the redux state.
 */

import { connectRouter, RouterState } from 'connected-react-router';
import { Action, AnyAction, combineReducers, Dispatch } from 'redux';
import { all, fork } from 'redux-saga/effects';
import { authReducer } from './auth/reducer';
import authSaga from './auth/sagas';
import { AuthState } from './auth/types';
import { commonReducer } from './common/reducer';
import commonSaga from './common/sagas';
import { AppCommonState } from './common/types';
import { rolesReducer } from './roles/reducer';
import rolesSaga from './roles/sagas';
import { RolesState } from './roles/types';
import { singleObjectReducer } from './singleObject/reducer';
import singleObjectSaga from './singleObject/sagas';
import { SingleObjectState } from './singleObject/types';
import { storedRouteReducer } from './storedRoute/reducer';
import { StoredRouteState } from './storedRoute/types';
import { treeLayoutReducer } from './treeLayout/reducer';
import treeLayoutSaga from './treeLayout/sagas';
import { TreeLayoutState } from './treeLayout/types';

// The top-level state object
export interface ApplicationState {
    app: AppCommonState;
    auth: AuthState;
    storedRoute: StoredRouteState;
    roles: RolesState;
    singleObject: SingleObjectState;
    treeLayout: TreeLayoutState;

    router: RouterState;
}

// Additional props for connected React components. This prop is passed by default with `connect()`
export interface ConnectedReduxProps<A extends Action = AnyAction> {
    dispatch: Dispatch<A>;
}

// Whenever an action is dispatched, Redux will update each top-level application state property
// using the reducer with the matching name. It's important that the names match exactly, and that
// the reducer acts on the corresponding ApplicationState property type.
export const createRootReducer = (history: History) =>
    combineReducers({
        app: commonReducer,
        auth: authReducer,
        storedRoute: storedRouteReducer,
        roles: rolesReducer,
        singleObject: singleObjectReducer,
        treeLayout: treeLayoutReducer,
        router: connectRouter(history),
    });

// Here we use `redux-saga` to trigger actions asynchronously. `redux-saga` uses something called a
// "generator function", which you can read about here:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function*
export function* rootSaga() {
    // yield all([fork(heroesSaga), fork(teamsSaga)]);
    yield all([
        fork(authSaga),
        fork(rolesSaga),
        fork(commonSaga),
        fork(singleObjectSaga),
        fork(treeLayoutSaga),
    ]);
}
